import type { i18n, ThirdPartyModule } from 'i18next'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function isControlLanguageModule(module: ThirdPartyModule): module is ControlLanguageModule {
  return module.type === '3rdParty' && 'name' in module && module.name === 'ControlLanguageModule'
}

/**
 * ControlLanguageModule is a third-party module for i18next that allows controlling
 * whether the language is controlled by the application or not.
 * It emits an event when the language control state changes.
 */
export class ControlLanguageModule implements ThirdPartyModule {
  readonly name = 'ControlLanguageModule'
  readonly type = '3rdParty' as const

  private _i18n: i18n | undefined
  private _isLanguageControlled = false

  init(i18next: i18n): void {
    this._i18n = i18next
  }

  setIsLanguageControlled(isLanguageControlled: boolean): void {
    if (this._isLanguageControlled === isLanguageControlled) {
      return
    }

    // console.debug(`ControlLanguageModule: set isLanguageControlled to ${isLanguageControlled}`)

    this._isLanguageControlled = isLanguageControlled
    this._i18n?.emit('languageIsControlled', isLanguageControlled)
  }

  get isLanguageControlled(): boolean {
    return this._isLanguageControlled
  }
}

export const useIsLanguageControlled = () => {
  const { i18n } = useTranslation()
  const languageControlledModule = i18n.modules.external.find(isControlLanguageModule)
  const [isLanguageControlled, setIsLanguageControlled] = useState(languageControlledModule?.isLanguageControlled ?? false)

  useEffect(() => {
    i18n.on('languageIsControlled', (isLanguageControlled: boolean) => {
      console.debug(`Language control state changed: ${isLanguageControlled}`)
      setIsLanguageControlled(isLanguageControlled)
    })

    setIsLanguageControlled(languageControlledModule?.isLanguageControlled ?? false)

    return () => {
      i18n.off('languageIsControlled', languageControlledModule?.setIsLanguageControlled)
    }
  }, [i18n.on, i18n.off, languageControlledModule])

  return isLanguageControlled
}
